import { nowStamp, today } from '../core/format/dates'
import { countSummary, mergeReceiptLines, receiptFileProblem, receiptProblems, receiptTotalCents, validCount } from '../core/rules/stockCount'
import { db, type StockReceiptRow } from './db'
import { bookStock, fail, itemIds, levelsOf, rowOf, toItem } from './stockBackend'
import { demoId, reply } from './reply'
import { averageCost } from '../core/rules/margin'
import { demoAdminIdentity } from './adminIdentity'
import type { AdminBackend } from '../core/types/backend'
import type { PurchaseReceipt, StockCountConflict, StockItem } from '../core/types/stock'

/**
 * DEMO — inkoopfacturen en tellingen (docs/VOORRAAD.md). Elke regel wordt een gewone mutatie via
 * `bookStock`, zodat stand, log en Omzet altijd kloppen.
 *
 * De bijlage van een factuur bewaart de demo niet in de browseropslag (te groot): alleen naam,
 * grootte en soort. Tijdens deze sessie is hij wel te openen (een tijdelijke link in het geheugen).
 * De server bewaart hem privé en geeft een getekende link terug.
 */


/** Bijlagen van deze sessie: receipt-id → tijdelijke link. */
const sessionFiles = new Map<string, string>()

const toReceipt = ({ requestId: _r, ...row }: StockReceiptRow): PurchaseReceipt => ({
  ...row,
  ...(row.file ? { file: { ...row.file, ...(sessionFiles.has(row.id) ? { url: sessionFiles.get(row.id) } : {}) } } : {}),
})

const norm = (s: string) => s.trim().toLowerCase()

export const demoStockReceiptsCountsBackend: Pick<AdminBackend, 'fetchStockReceipts' | 'addStockReceipt' | 'fetchStockCounts' | 'addStockCount'> = {
  fetchStockReceipts() {
    const list = db
      .stockReceipts()
      .sort((a, b) => b.bookedAt.localeCompare(a.bookedAt))
      .map(toReceipt)
    return reply(list, 300)
  },

  async addStockReceipt(req) {
    // Al geboekt met dit requestId: hetzelfde antwoord, niets dubbel.
    const known = db.stockReceipts().find((r) => r.requestId === req.requestId)
    if (known) return reply(toReceipt(known), 250)

    const problems = receiptProblems(req, today())
    const first = Object.entries(problems)[0]
    if (first) {
      await reply(null, 250)
      return fail(first[1], 422, 'invalid', first[0])
    }
    if (req.file) {
      const fileProblem = receiptFileProblem(req.file)
      if (fileProblem) {
        await reply(null, 250)
        return fail(fileProblem, 422, 'invalid', 'file')
      }
    }
    if (req.invoiceTotalCents !== undefined && (!Number.isInteger(req.invoiceTotalCents) || req.invoiceTotalCents < 0)) {
      return fail('Vul een geldig factuurbedrag in.', 422, 'invalid', 'invoiceTotalCents')
    }
    if (req.invoiceVatCents !== undefined && (!Number.isInteger(req.invoiceVatCents) || req.invoiceVatCents < 0)) {
      return fail('Vul een geldig btw-bedrag in.', 422, 'invalid', 'invoiceVatCents')
    }
    const lines = mergeReceiptLines(req.lines)
    const missing = lines.find((l) => !toItem(l.itemId))
    if (missing) return fail('Een artikel op de factuur bestaat niet (meer). Haal die regel weg.', 422, 'invalid', 'lines')
    if (db.stockReceipts().some((r) => norm(r.supplier) === norm(req.supplier) && norm(r.invoiceNumber) === norm(req.invoiceNumber))) {
      await reply(null, 250)
      return fail(`Factuur ${req.invoiceNumber} van ${req.supplier.trim()} is al geboekt.`, 409, 'receipt_duplicate', 'invoiceNumber')
    }

    // Eén "transactie": eerst de inkoopprijzen, dan per regel een inkoopmutatie. De inkoopprijs
    // wordt een gewogen gemiddelde met wat er al lag; een gratis regel (sample, 100% korting) laat
    // het gemiddelde staan (core/rules/margin.ts `averageCost`, LARAVEL.md §9.8).
    // Salongebruik en samples tellen niet in de winkelmarge: die laten het gemiddelde staan.
    lines.forEach((l) => {
      if (l.use === 'salon' || l.use === 'sample') return
      const row = rowOf(l.itemId) ?? { id: l.itemId, kind: 'retail' as const, salon: null }
      const levels = levelsOf(l.itemId)
      const onHand = Math.max(0, (levels.webshop ?? 0) + (levels.salon ?? 0))
      const next = averageCost(onHand, row.costPriceCents, l.qty, l.costPriceCents)
      if (next !== undefined && next !== row.costPriceCents) db.putStockItem({ ...row, costPriceCents: next })
    })
    const supplier = req.supplier.trim()
    const invoiceNumber = req.invoiceNumber.trim()
    lines.forEach((l) =>
      bookStock(l.itemId, {
        reason: 'inkoop',
        location: req.location,
        qty: l.qty,
        ref: invoiceNumber,
        note: `Inkoopfactuur ${supplier}${l.use === 'sample' ? ' · gratis sample' : l.use === 'salon' ? ' · salongebruik' : ''}`,
      }),
    )

    const id = demoId('inkoop').toLowerCase()
    const row: StockReceiptRow = {
      id,
      requestId: req.requestId,
      supplier,
      invoiceNumber,
      invoiceDate: req.invoiceDate,
      location: req.location,
      lines: lines.map((l) => ({ ...l, name: toItem(l.itemId)?.name ?? l.itemId })),
      totalCostCents: receiptTotalCents(lines),
      ...(req.invoiceTotalCents !== undefined ? { invoiceTotalCents: req.invoiceTotalCents } : {}),
      ...(req.invoiceVatCents !== undefined ? { invoiceVatCents: req.invoiceVatCents } : {}),
      ...(req.note?.trim() ? { note: req.note.trim() } : {}),
      ...(req.file ? { file: { name: req.file.name, size: req.file.size, type: req.file.type } } : {}),
      bookedAt: nowStamp(),
      ...demoAdminIdentity.actor(),
    }
    if (req.file) sessionFiles.set(id, URL.createObjectURL(req.file))
    db.addStockReceipt(row)
    return reply(toReceipt(row), 600)
  },

  fetchStockCounts() {
    const list = db
      .stockCounts()
      .sort((a, b) => b.at.localeCompare(a.at))
      .map(({ requestId: _r, ...c }) => c)
    return reply(list, 250)
  },

  async addStockCount(req) {
    const known = db.stockCounts().find((c) => c.requestId === req.requestId)
    if (known) {
      const { requestId: _r, ...count } = known
      return reply({ count, items: [], conflicts: [] }, 250)
    }
    if (req.lines.length === 0) return fail('Tel minstens één artikel.', 422, 'invalid', 'lines')
    const bad = req.lines.find((l) => !validCount(l.counted) || !toItem(l.itemId))
    if (bad) return fail('Een geteld aantal klopt niet. Vul 0 of meer in.', 422, 'invalid', `lines.${bad.itemId}`)

    const id = demoId('telling').toLowerCase()
    const conflicts: StockCountConflict[] = []
    const booked = req.lines.filter((l) => {
      const current = levelsOf(l.itemId)[req.location]
      if (current === l.expected) return true
      conflicts.push({ itemId: l.itemId, name: toItem(l.itemId)?.name ?? l.itemId, expected: l.expected, current })
      return false
    })
    booked.forEach((l) => {
      const diff = l.counted - (l.expected ?? 0)
      // Van "niet bijhouden" naar een getal: altijd een regel, zodat te zien is wanneer het begon.
      if (diff === 0 && l.expected !== null) return
      bookStock(l.itemId, { reason: 'telling', location: req.location, qty: l.expected === null ? l.counted : diff, ref: id, ...(req.note?.trim() ? { note: req.note.trim() } : {}) })
    })

    // Volledig = alles wat op deze plek bijgehouden werd, is geteld en geboekt.
    const trackedHere = itemIds().filter((itemId) => toItem(itemId) && levelsOf(itemId)[req.location] !== null)
    const countedIds = new Set(booked.map((l) => l.itemId))
    const complete = conflicts.length === 0 && trackedHere.every((itemId) => countedIds.has(itemId))

    const summary = countSummary(booked, (itemId) => rowOf(itemId)?.costPriceCents)
    const count = { id, location: req.location, at: nowStamp(), ...demoAdminIdentity.actor(), counted: booked.length, complete, ...summary }
    db.addStockCount({ ...count, requestId: req.requestId })
    // De actuele stand van alles wat geraakt is (ook de conflicten), zodat het scherm meteen klopt.
    const items = [...booked.map((l) => l.itemId), ...conflicts.map((c) => c.itemId)].map(toItem).filter((i): i is StockItem => i !== null)
    return reply({ count, items, conflicts }, 500)
  },
}
